import { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, AlertTriangle, CheckCircle, Clock, XCircle, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AlertStatus = 'pending' | 'resolved' | 'dismissed' | 'approved' | 'rejected';

export interface ActionCardProps {
  id: string;
  type: string;
  title: string;
  description?: string;
  severity: AlertSeverity;
  status: AlertStatus;
  moneyImpact?: number;
  evidence?: string[];
  actionRequired?: boolean;
  createdAt: string;
  onApprove?: () => void;
  onReject?: () => void;
  onDismiss?: () => void;
  onViewEvidence?: () => void;
  icon?: ReactNode;
}

const severityConfig: Record<AlertSeverity, { color: string; bgColor: string; label: string; border: string }> = {
  low: { color: 'text-muted-foreground', bgColor: 'bg-muted/50', label: 'Low', border: 'border-l-muted' },
  medium: { color: 'text-warning', bgColor: 'bg-warning/10', label: 'Medium', border: 'border-l-warning' },
  high: { color: 'text-destructive', bgColor: 'bg-destructive/10', label: 'High', border: 'border-l-destructive/70' },
  critical: { color: 'text-destructive', bgColor: 'bg-destructive/15', label: 'Critical', border: 'border-l-destructive' },
};

const statusIcons: Record<AlertStatus, ReactNode> = {
  pending: <Clock className="h-4 w-4 text-warning" />,
  resolved: <CheckCircle className="h-4 w-4 text-success" />,
  dismissed: <XCircle className="h-4 w-4 text-muted-foreground" />,
  approved: <CheckCircle className="h-4 w-4 text-success" />,
  rejected: <XCircle className="h-4 w-4 text-destructive" />,
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

const formatRelativeTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export function ActionCard({
  id,
  type,
  title,
  description,
  severity,
  status,
  moneyImpact,
  evidence,
  actionRequired,
  createdAt,
  onApprove,
  onReject,
  onDismiss,
  onViewEvidence,
  icon,
}: ActionCardProps) {
  const config = severityConfig[severity];
  const isPending = status === 'pending';

  return (
    <Card
      className={cn(
        'p-5 transition-all hover:shadow-card-hover border-l-4 rounded-3xl',
        config.border
      )}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={cn('p-3 rounded-2xl shrink-0', config.bgColor)}>
          {icon || <AlertTriangle className={cn('h-5 w-5', config.color)} />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{title}</h3>
              {actionRequired && isPending && (
                <Badge variant="outline" className="text-xs border-warning text-warning rounded-xl">
                  Action Required
                </Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground shrink-0">{formatRelativeTime(createdAt)}</span>
          </div>

          {description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{description}</p>
          )}

          {/* Money Impact & Evidence */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {moneyImpact !== undefined && moneyImpact !== 0 && (
              <Badge variant="secondary" className="font-mono rounded-xl">
                {moneyImpact > 0 ? '+' : ''}{formatCurrency(moneyImpact)}
              </Badge>
            )}
            {evidence && evidence.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground rounded-xl"
                onClick={onViewEvidence}
              >
                <Eye className="h-3 w-3 mr-1.5" />
                {evidence.length} evidence point{evidence.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>

          {/* Actions */}
          {isPending && actionRequired && (
            <div className="flex items-center gap-3">
              {onApprove && (
                <Button size="sm" onClick={onApprove} className="h-9 rounded-2xl px-5">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              )}
              {onReject && (
                <Button size="sm" variant="destructive" onClick={onReject} className="h-9 rounded-2xl px-5">
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              )}
              {onDismiss && (
                <Button size="sm" variant="ghost" onClick={onDismiss} className="h-9 rounded-2xl">
                  Dismiss
                </Button>
              )}
            </div>
          )}

          {!isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {statusIcons[status]}
              <span className="capitalize">{status}</span>
            </div>
          )}
        </div>

        {/* Chevron for navigation */}
        <ChevronRight className="h-5 w-5 text-muted-foreground/50 shrink-0" />
      </div>
    </Card>
  );
}
